import React, { useEffect, useMemo, useState } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MuiTooltip from "@mui/material/Tooltip";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { apiCallgetCategoryScore } from "../../../Core/Apicall";

ChartJS.register(ArcElement, Tooltip, Legend);

type FunnelFilter =
  | "today"
  | "yesterday"
  | "last7days"
  | "lastMonth"
  | "lastYear";

export const FUNNEL_FILTERS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7days", label: "Last 7 Days" },
  { key: "lastMonth", label: "Last 30 Days" },
  { key: "lastYear", label: "Last Year" },
];

export default function CategoryScore({ venueId }: { venueId: string }) {

  const [filter, setFilter] = useState<FunnelFilter>("lastYear");
  const [loading, setLoading] = useState(false);
  const [overallAvgRating, setOverallAvgRating] = useState(0);
  const [overallResponses, setOverallResponses] = useState(0);
  const [categoryData, setCategoryData] = useState<any[]>([]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      const res = await apiCallgetCategoryScore({ venueId, filter });

      if (res?.status) {
        setCategoryData(res.data || []);
        setOverallAvgRating(res.overallAvgRating || 0);
        setOverallResponses(res.overallAvgResponses || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [filter]);

  return (
    <div style={pageWrapper}>

      {/* FILTERS */}
      <div style={filterContainer}>
        {FUNNEL_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key as FunnelFilter)}
            style={{
              ...filterButton,
              ...(filter === f.key ? activeFilter : {})
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* OVERALL CARD */}
      <div style={overallCard}>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            marginBottom: 20
          }}
        >
          <h3
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: "#111827",
              margin: 0
            }}
          >
            All category average rating
          </h3>

          <MuiTooltip
            title="Average rating given by guests across all categories for the selected time period."
            arrow
          >
            <InfoOutlinedIcon
              style={{
                fontSize: 18,
                color: "#9CA3AF",
                cursor: "pointer"
              }}
            />
          </MuiTooltip>
        </div>

        <div style={chartBox}>
          <Doughnut
            data={createChartData(overallAvgRating)}
            options={chartOptions}
          />

          <div style={centerText}>
            <h2 style={ratingNumber}>{overallAvgRating.toFixed(1)}</h2>
            <p style={ratingLabel}>/ 5 Rating</p>
          </div>
        </div>

        <p style={responseText}>{overallResponses} responses</p>

      </div>

      {/* CATEGORY CARDS */}
      <div style={categoryGrid}>

        {categoryData.map((c) => (
          <div key={c.category} style={categoryCard}>

            <h4 style={{
              fontSize: 18,
              fontWeight: 600,
              marginBottom: 10,
              color: "#111827"
            }}>
              {c.category}
            </h4>

            <div style={chartBoxSmall}>
              <Doughnut
                data={createChartData(c.avgRating)}
                options={chartOptions}
              />

              <div style={centerText}>
                <h3 style={{ margin: 0 }}>{c.avgRating}</h3>
                <p style={{ fontSize: 12 }}>/ 5</p>
              </div>
            </div>

            <p style={responseText}>{c.totalResponses} responses</p>

          </div>
        ))}

      </div>

    </div>
  );
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: { enabled: false }
  },
  elements: {
    arc: {
      borderRadius: 10
    }
  }
};

const getRatingColor = (rating: number) => {
  if (rating >= 4) return "#22C55E";
  if (rating >= 3) return "#F59E0B";
  return "#EF4444";
};

const createChartData = (rating: number) => {
  const color = getRatingColor(rating);

  return {
    datasets: [
      {
        data: [rating, 5 - rating],
        backgroundColor: [color, "#EEF2F7"],
        borderWidth: 1,
        cutout: "70%"
      }
    ]
  };
};


const filterContainer: React.CSSProperties = {
  display: "flex",
  gap: 12,
  marginBottom: 24,
  flexWrap: "wrap",
  justifyContent: "flex-end",
};



const centerText: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  textAlign: "center",
  pointerEvents: "none",
};


const filterButton: React.CSSProperties = {
  padding: "8px 20px",
  borderRadius: 30,
  border: "1px solid #E5E7EB",
  background: "#F9FAFB",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 14,
  transition: "all 0.2s ease",
};

const activeFilter: React.CSSProperties = {
  background: "linear-gradient(135deg, #3B82F6, #2563EB)",
  color: "#fff",
  border: "none",
  boxShadow: "0 6px 16px rgba(37,99,235,0.35)",
};

const pageWrapper: React.CSSProperties = {
  padding: 40,
  background: "#F3F4F6",
  minHeight: "100vh",
  fontFamily: "Inter, sans-serif"
};



const overallCard: React.CSSProperties = {
  background: "#fff",
  padding: 30,
  borderRadius: 18,
  marginBottom: 30,
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  textAlign: "center"
};

const categoryGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))",
  gap: 24
};

const categoryCard: React.CSSProperties = {
  background: "#fff",
  padding: 26,
  borderRadius: 18,
  textAlign: "center",
  boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
  transition: "all 0.25s ease",
  border: "1px solid #F1F5F9",
  cursor: "pointer"
};
const chartBox: React.CSSProperties = {
  width: 240,
  height: 240,
  margin: "0 auto",
  position: "relative"
};

const chartBoxSmall: React.CSSProperties = {
  width: 160,
  height: 160,
  margin: "10px auto",
  position: "relative",
};

const ratingNumber: React.CSSProperties = {
  margin: 0,
  fontSize: 44,
  fontWeight: 700,
  color: "#111827",
  letterSpacing: "-0.02em"
};

const ratingLabel: React.CSSProperties = {
  margin: 0,
  fontSize: 14,
  color: "#6B7280",
  fontWeight: 500
};

const responseText: React.CSSProperties = {
  marginTop: 12,
  fontSize: 13,
  color: "#9CA3AF",
  fontWeight: 500
};
